import { REAL_TIME_COMMUNICATION_PLATFORMS } from '@this+that/constants';
import {
  AgentDelegationKind,
  InboxEntry,
  MessagingPlatform,
  UserCommunication,
} from '@this+that/models';

import type {
  DelegateInputContext,
  DoBoxInputContext,
} from '../../../../store/FloatingAssistant/types';

const EMAIL_PREVIEW_MAX_LENGTH = 120;
const DOBOX_PREVIEW_MAX_LENGTH = 120;

function resolveSourceFromPlatform(
  platform: MessagingPlatform | null | undefined,
): DelegateInputContext['source'] {
  if (
    platform != null &&
    REAL_TIME_COMMUNICATION_PLATFORMS.includes(platform)
  ) {
    return 'slack';
  }
  return 'email';
}

export function buildEmailDelegateContext(
  userCommunication: UserCommunication,
): DelegateInputContext {
  return {
    kind: 'delegate',
    source: resolveSourceFromPlatform(userCommunication.platform),
    itemId: String(userCommunication.messageId),
    author: userCommunication.author ?? userCommunication.authorEmail ?? '',
    subject: userCommunication.subject ?? undefined,
    preview:
      userCommunication.messagePreview ??
      userCommunication.messageText?.slice(0, EMAIL_PREVIEW_MAX_LENGTH) ??
      '',
  };
}

// Only a delegation the current user started, with a conversation already running,
// tells the hosted panel which conversation to keep focused. It never carries the
// agent: a message sent through the hosted shell is always the default assistant's.
function resolveDelegationConversationContext(
  inboxEntry: InboxEntry,
  userId: string | undefined,
): Pick<DoBoxInputContext, 'delegationConversationId'> {
  const delegation = inboxEntry.agentDelegation;
  if (
    delegation?.kind !== AgentDelegationKind.AGENT_RUN ||
    delegation.agentTemplateId == null ||
    delegation.conversationId == null ||
    delegation.delegatedByUserId !== userId
  ) {
    return {};
  }
  return {
    delegationConversationId: delegation.conversationId,
  };
}

export function buildDoBoxDelegateContext(
  inboxEntry: InboxEntry,
  userId?: string,
): DoBoxInputContext {
  return {
    kind: 'dobox',
    itemId: inboxEntry.id,
    taskGroupId: inboxEntry.taskGroupId,
    subject: inboxEntry.subject ?? '',
    preview: (inboxEntry.description ?? inboxEntry.subject ?? '').slice(
      0,
      DOBOX_PREVIEW_MAX_LENGTH,
    ),
    ...resolveDelegationConversationContext(inboxEntry, userId),
  };
}
